import { AxiosResponse } from "axios";
import React, { ReactElement } from "react";
import { WintSuperkollAPIModel, WintUtilsWebApi } from "wint-api-client/dist/types";
import { ComponentBaseProps } from "models/propTypes/basePropTypes";
import { IconDefinition } from "models/propTypes/iconPropTypes";
import { TableEnums } from "models/enums";
import { MoreActionMenuItem } from "models/propTypes/menuPropTypes";

export interface TableRowCommonKeys {
  group: string;
  select: any;
  loading: boolean;
}

export type ListAPIResponse<RowType> = Promise<AxiosResponse<WintUtilsWebApi.ListResponse<RowType>> & { key?: number }>;

export interface TableHandles {
  exportToExcel: () => void;
  exportToCSV: () => void;
  exportToPDF: () => void;
  exportRawDataToExcel: () => void;
  exportRawDataToCSV: () => void;
  importCSVToArticle: () => String;
  getApiFilter?: (page: number, overrideRowsPerPage?: number) => WintUtilsWebApi.Filter;
}

export interface TableAsyncProps<RowType extends Record<string, any>> extends ComponentBaseProps {
  /** Attach a ref to submit form from outside */
  tableRef?: ((instance: TableHandles | null) => void) | React.MutableRefObject<TableHandles | null> | null;
  /** Optional unique id for the table */
  tableId?: string;
  /** Column definition */
  properties: Partial<Record<keyof (RowType & TableRowCommonKeys), TableRowProperties>>;
  /** Table rows as a static array or a function to API returning a WintUtilsWebApi.ListResponse. Note: Use a memoized function or array */
  filter:
    | (<T extends WintUtilsWebApi.Filter>(
        filter: T,
        key: number,
        skipUndefinedConversion?: boolean
      ) => ListAPIResponse<RowType>)
    | RowType[];
  /** Click handler for a single row */
  rowClickHandler?: (
    row: any,
    rowIndex: number,
    page?: number,
    rowsPerPage?: number,
    getApiFilter?: (page: number) => WintUtilsWebApi.Filter
  ) => void;
  /** Hover actions for a single row */
  rowHoverActionsHandler?: (row: any, rowIndex: number) => MoreActionMenuItem[];
  /** Maximum row count to display in a single page */
  maxRowCount?: number;
  /** Initial column to be sorted by */
  orderByProperty?: keyof RowType;
  /** Initial column sort order */
  orderByDescending?: boolean;
  /** Second column to be sorted by */
  thenByProperty?: keyof RowType;
  /** Second column sort order */
  thenByDescending?: boolean;
  /** Disable grouping of rows when sorted by this property */
  disableGroupWhenOrderBy?: (keyof RowType)[];
  /** Property name to use for grouping rows when sorting. When specified, this becomes the primary sort field and user-selected column becomes secondary */
  groupByProperty?: keyof RowType;
  /** Sort order for groupByProperty. Defaults to false (ascending) */
  groupByDescending?: boolean;
  /** Disable column sorting. Enabled by default */
  disableSort?: boolean;
  /** Enable pagination. Enabled by default. If advanced pagination is selected, users are allowed to change max row count per page  */
  pagination?: boolean | "advanced";
  /** Include a summary to a table*/
  includeSummary?: boolean;
  /** Fetch summary data along with the main data in a single request. When true, the filter function will be called once with includeSummary=true instead of two separate requests. Requires includeSummary to be true. */
  fetchSummaryWithData?: boolean;
  /** Used to override the default summary component */
  customSummaryInfo?: string | ReactElement | ((apiResultItem: any) => string | ReactElement);
  /** Used to override the default summary component, while excel/csv/pdf export */
  customSummaryInfoForExport?: string | ((apiResultItem: any) => string | undefined);
  /** Hide table header  */
  hideHeader?: boolean;
  /** Option to hide pagination when the results contain only one page  */
  hidePaginationIfSinglePage?: boolean;
  /** Option to use alternating colours to table rows  */
  alternateColors?: boolean;
  /** Should be a memoized value i.e stored in a state by the parent */
  reloadKey?: number;
  onRowSelectionsChange?: (selectedRows: RowType[]) => void;
  allowCheckBoxSelection?: boolean;
  disableCheckBoxSelectionForRows?: boolean;
  hiddenColumns?: (keyof RowType)[];
  itemUrl?: (id: string | number) => string;
  disableQueryPagination?: boolean;
  stayOnCurrentPage?: boolean;
  /** Returns to the first page whenever this value changes, even when stayOnCurrentPage is set */
  pageResetKey?: string | number;
  /** Page to open on when pagination is kept in component state (disableQueryPagination) */
  initialPage?: number;
  onCurrentPageChange?: (page: number) => void;
  /** File name to be used when exporting table content */
  exportFileName?: string;
  /** Additional styles to be applied when creating table content rows */
  rowClasses?: string | ((row: any) => string);
  /** A description to be printed on the pdf when exporting table content */
  exportFileDescription?: string;
  /** If to show smaller header text in pdf*/
  pdfHeaderTextSmall?: boolean;
}

export interface TableRowProps extends ComponentBaseProps {
  row: any;
  index: number;
  rowClickHandler?: (row: any, rowIndex: number) => void;
  toggleSelectionHandler: (rowIndex: number) => void;
  rowHoverActions?: MoreActionMenuItem[];
  columns: [string, TableRowProperties | undefined][];
  allowCheckBoxSelection?: boolean;
  disableCheckBoxSelection?: boolean;
  rowClasses?: string | ((row: any) => string);
}

export interface NestedTableHandles {
  expandAll: () => void;
  collapseAll: () => void;
  expandIndex: (index: number, path?: number[]) => void;
}

export interface NestedTableProps<RowType, RepeatType> extends ComponentBaseProps {
  tableRef?: ((instance: NestedTableHandles | null) => void) | React.MutableRefObject<NestedTableHandles | null> | null;
  /** Optional unique id for the table. Need to be defined if table specific events are performed */
  tableId?: string;
  properties: Partial<Record<keyof (RowType & NestedTableRowCommonKeys), TableRowProperties>>;
  rowIcon?: (row: any) => IconDefinition | string;
  repeatProperties?: TableRepeatColumnProperties<RowType, RepeatType>;
  childrenKey: keyof RowType | ((parent: any) => Promise<any[]>);
  hasChildren?: (row: any) => boolean;
  rowDisplayType?: (row: any) => TableEnums.RowDisplayType | undefined;
  rows: any[];
  rowClickHandler?: (row: any, rowIndex: number, depth?: number) => void;
  hasMultiLevels?: boolean;
  allowCheckBoxSelection?: boolean;
  onRowSelectionsChange?: (selectedRows: RowType[]) => void;
  onRowsUpdate?: (
    previous: (RowType & NestedTableRowCommonKeys)[],
    next: (RowType & NestedTableRowCommonKeys)[]
  ) => (RowType & NestedTableRowCommonKeys)[];
  hiddenColumns?: (keyof RowType | keyof RepeatType)[];
  /** Option to hide pagination when the results contain only one page  */
  hidePaginationIfSinglePage?: boolean;
  /** Maximum row count to display in a single page */
  maxRowCount?: number;
  /** Enable pagination. Enabled by default. If advanced pagination is selected, users are allowed to change max row count per page  */
  pagination?: boolean | "advanced";
  checkBoxesCheckedAsDefault?: boolean;
  initialExpansionRowIndex?: number;
  /** Optional row footer renderer function that receives a row and returns a React component. Only displays for 1st level expanded rows */
  rowFooterRenderer?: (row: RowType & NestedTableRowCommonKeys, rowIndex: number) => React.ReactElement | null;
}

export interface NestedTableRowProps {
  row: any;
  index: number;
  parentIndex: number;
  path: number[];
  depth: number;
  childrenKey: string;
  secondaryChildrenKey?: string;
  hasChildren?: (row: any) => boolean;
  columns: [string, TableRowProperties | undefined][];
  repeatProperties?: TableRepeatColumnProperties<any, any>;
  repeatColumnLength: number;
  hasMultiLevels?: boolean;
  allowCheckBoxSelection?: boolean;
  rowDisplayType?: (row: any) => TableEnums.RowDisplayType | undefined;
  rowIcon?: (row: any) => IconDefinition | string;
  rowClickHandler?: (row: any, rowIndex: number, depth?: number) => void;
  toggleExpansionHandler: (rowIndex: number, parentIndex: number, path: number[]) => void;
  toggleSelectionHandler: (rowIndex: number) => void;
  hiddenColumns?: string[];
  checkBoxesCheckedAsDefault?: boolean;
  rowFooterRenderer?: (row: any, rowIndex: number) => React.ReactElement | null;
}

export interface DrillDownTableProps<RowType, RepeatType> extends ComponentBaseProps {
  tableRef?: ((instance: NestedTableHandles | null) => void) | React.MutableRefObject<NestedTableHandles | null> | null;
  /** Optional unique id for the table. Need to be defined if table specific events are performed */
  tableId?: string;
  rowTextProperty: {
    key: keyof RowType & NestedTableRowCommonKeys;
    properties: TableRowProperties;
  };
  repeatProperties?: DrillDownTableRepeatColumnProperties<RowType, RepeatType>;
  childrenKey: keyof RowType;
  hasChildren?: (row: any) => boolean;
  rowDisplayType?: (row: any) => TableEnums.RowDisplayType | undefined;
  rows: any[];
  rowClickHandler?: (row: any, rowIndex: number) => void;
  hiddenColumns?: (keyof RowType | keyof RepeatType)[];
  showTotal?: {
    key: keyof any;
    valueFormatter: (values: any, rowIndex: number, depth?: number, repeatIndex?: number, parentRow?: any) => number;
    displayFormatter?: (value: number) => any;
  };
}

export interface DrillDownTableBodyProps<RowType, RepeatType> extends DrillDownTableProps<RowType, RepeatType> {
  repeatColumnLength: number;
  autoExpandProps?: {
    expandAll?: boolean;
    expandIndex?: {
      index: number;
      path: number[];
    };
  };
}
export interface DrillDownTableHeadProps<RowType, RepeatType> extends DrillDownTableProps<RowType, RepeatType> {
  repeatColumnLength: number;
}

export interface TableExpansionProps {
  expandAll?: boolean;
  expandIndex?: {
    index: number;
    path: number[];
  };
}
export interface DrillDownTableBaseRowProps {
  row: any;
  index: number;
  parentIndex: number;
  path: number[];
  depth: number;
  childrenKey: keyof any;
  rowTextProperty: {
    key: keyof any & NestedTableRowCommonKeys;
    properties: TableRowProperties;
  };
  repeatPropertyKey?: string;
  repeatPropertiesList?: [string, TableRowProperties | undefined][];
  repeatPropertiesLength: number;
  repeatColumnLength: number;
  showTotal?: {
    key: keyof any;
    valueFormatter: (values: any, rowIndex: number, depth?: number, repeatIndex?: number, parentRow?: any) => number;
    displayFormatter?: (value: number) => any;
  };
  className?: string;
  expandable?: boolean;
  inlineExpansion?: boolean;
  expanded?: boolean;
  onClickRow?: () => void;
  sumValueRow?: any;
  sumNegative?: (repeatColumns: any) => boolean;
  autoExpandProps?: TableExpansionProps;
  noExpandableSiblingRows?: boolean;
}

export interface DrillDownTableRowProps extends DrillDownTableBaseRowProps {
  rowDisplayType?: (row: any) => TableEnums.RowDisplayType | undefined;
  autoExpandProps?: TableExpansionProps;
}

export interface DrillDownTableRowSwitchProps extends DrillDownTableBaseRowProps {
  displayType?: TableEnums.RowDisplayType;
}

export interface DrillDownTableChildrenRowsProps extends DrillDownTableRowProps {
  rows: any[];
}

export interface DrillDownTableEmptyRowProps {
  text?: string;
  repeatPropertyKey?: string;
  repeatPropertiesList?: [string, TableRowProperties | undefined][];
  repeatPropertiesLength: number;
  repeatColumnLength: number;
  showTotal?: boolean;
  className?: string;
  sumValueRow?: any;
  sumNegative?: (repeatColumns: any) => boolean;
}

export interface RowTitleTextCellProps {
  row: any;
  index: number;
  depth: number;
  propertyName: string;
  cellValue: TableRowProperties;
  className?: string;
  colSpan?: number;
  inlineExpansion?: boolean;
  expanded?: boolean;
}

export interface RepeatCellsProps {
  index: number;
  depth: number;
  row: any;
  repeatPropertyKey: string;
  repeatPropertiesList: [string, TableRowProperties | undefined][];
  repeatColumnLength: number;
  repeatPropertiesLength: number;
  sumValueRow?: any;
  sumNegative?: (repeatColumns: any) => boolean;
  showTotal?: boolean;
}

export interface ExpandableCellProps {
  expanded: boolean;
  className?: string;
}

export interface TotalCellProps {
  index: number;
  depth: number;
  row: any;
  repeatPropertyKey: string;
  repeatColumnLength: number;
  showTotal: {
    key: keyof any;
    valueFormatter: (values: any, rowIndex: number, depth?: number, repeatIndex?: number, parentRow?: any) => number;
    displayFormatter?: (value: number) => any;
  };
}

export interface DrillDownTableRepeatColumnProperties<RowType, T> {
  key: keyof RowType;
  properties: Partial<Record<keyof T, TableRowProperties>>;
  filterField?: (repeatIndex: number, loading?: boolean) => string | React.ReactElement;
  addRepeatColumnField?: (repeatIndex: number) => React.ReactElement;
  sumNegative?: (repeatColumns: any) => boolean;
  loading?: boolean;
}

export interface TableRepeatColumnProperties<RowType, T> {
  key: keyof RowType;
  properties: Partial<Record<keyof T, TableRowProperties>>;
  filterField?: (repeatIndex: number, loading?: boolean) => string | React.ReactElement;
  addRepeatColumnField?: (repeatIndex: number) => React.ReactElement;
  sumNegative?: (repeatColumns: any) => boolean;
  showTotal?: {
    key: keyof T;
    valueFormatter: (values: any, rowIndex: number, depth?: number, repeatIndex?: number, parentRow?: any) => number;
    displayFormatter: (value: number) => any;
  };
  loading?: boolean;
}

export interface NestedTableRowCommonKeys {
  expanded?: boolean;
  selected?: boolean;
  disabled?: boolean;
  group?: string;
}

export interface TableRowProperties extends ComponentBaseProps {
  /** Column header text */
  description?: string | ReactElement | ((repeatIndex: number) => string | ReactElement);
  /** Column header when export to excel, csv, pdf etc. Can be used to override column titles in exported files. It's mandatory that this has a value when description is not returing a string. */
  descriptionToPrint?: string;
  /** More information on what's in the column */
  helpText?: string;
  /** More information on what's in the column, shown in a tooltip on an info icon next to the header. Successor to helpText. */
  helpTooltip?: string;
  /** If column data needs to be formatted before displaying */
  formatter?: (values: any, rowIndex: number, depth?: number, repeatIndex?: number, parentRow?: any) => any;
  /** Column content when export to excel, csv, pdf etc. Can be used to override column content in exported files. It's mandatory that this has a value when formatter is not returing a string. */
  valueToPrint?: (values: any, rowIndex: number, depth?: number, repeatIndex?: number, parentRow?: any) => string;
  /** Alignment of the content */
  alignment?: ColumnAlignment;
  /** If to show smaller text */
  isTextSmall?: boolean;
  /** Can speficy a static width for the column */
  width?: number | string;
  /** Can speficy a static width for the pdf column */
  pdfColumnwidth?: number;
  /** Can speficy a max width for the column */
  maxWidth?: number | string;
  /** To avoid wrapping content when not enough spae to display */
  noWrap?: boolean;
  /** Can be defined when needs to format summary value of the column in the footer */
  summaryValueFormatter?: (values: any) => any;
  summaryToPrint?: (values: any) => any;
  /** Disable sorting for a column */
  disableSort?: boolean;
  expandOnClick?: boolean;
  /** Can be used to override Classification of column type. If the key ends with any of the values, we automatically classify the column as that type.
   * 1. Used to determine initial sort order and column type.
   *      Columns can be classified as number types to initial sort by descending.
   */
  classification?:
    | "date"
    | "month"
    | "year"
    | "time"
    | "number"
    | "amount"
    | "value"
    | "count"
    | "percentage"
    | "cost"
    | "price"
    | "quantity";
  /** Column visibility: Visible, Hidden, or ExportOnly */
  visibility?: ColumnVisibility;
}

export enum ColumnVisibility {
  /** The default. Column is displayed in the table and included in the export */
  Default = 0,
  /** Column is hidden in the table and not included in the export */
  Hidden = 1,
  /** Column is hidden in the table but included in the export */
  ExportOnly = 2,
}

export interface TablePaginationProps {
  total: number;
  currentPage: number;
  rowsPerPage: number;
  maxRowsPerPage?: number;
  advancedPagination?: boolean;
  hideIfSinglePage?: boolean;
  hideItemRange?: boolean;
  hideRowsPerPageSelector?: boolean;
  changePageHandler: (pageNumber: number) => void;
  changeRowsPerPageHandler: (rowCount: number) => void;
}

export interface TableCellCurrencyFormatterProps {
  sumValue?: number | WintSuperkollAPIModel.CurrencyValue;
  cellValue?: WintSuperkollAPIModel.CurrencyValue[];
  title?: string;
}

export interface SummaryTotalFormatterProps {
  total?: TableCellCurrencyFormatterProps;
  subTotals?: TableCellCurrencyFormatterProps[];
}

export interface TableContextState {
  tableKey: string;
  filter: WintUtilsWebApi.Filter;
  setFilter: <T extends WintUtilsWebApi.Filter>(filter: T) => void;
}

export interface TableContextProviderProps {
  tableKey: string;
  filter?: WintUtilsWebApi.Filter;
}

export type ColumnAlignment = "inherit" | "left" | "center" | "right" | "justify";

export type Order = "asc" | "desc";

export interface TableSubTextHeaderProps {
  title: string;
  subText: string;
}

export interface NestedTablePrintProps<RowType, RepeatType> extends ComponentBaseProps {
  title?: string;
  subTitle?: string;
  properties: Partial<Record<keyof (RowType & NestedTableRowCommonKeys), TableRowProperties>>;
  repeatProperties?: TableRepeatColumnProperties<RowType, RepeatType>;
  childrenKey: keyof RowType | ((parent: any) => Promise<any[]>);
  hasChildren?: (row: any) => boolean;
  rowDisplayType?: (row: any) => TableEnums.RowDisplayType | undefined;
  rows: any[];
  maxRepeatColumns?: number;
  repeatColumnDefaultWidth?: number;
}

export interface NestedTablePrintRowProps {
  row: any;
  index: number;
  path: number[];
  depth: number;
  childrenKey: string;
  hasChildren?: (row: any) => boolean;
  columns: [string, TableRowProperties | undefined][];
  repeatProperties?: TableRepeatColumnProperties<any, any>;
  rowDisplayType?: (row: any) => TableEnums.RowDisplayType | undefined;
  repeatColumnsLength: number;
  repeatColumnDefaultWidth: number;
  nonRepeatColumnWidth: number;
}

export interface TableAsyncPrintProps<RowType extends Record<string, any>> extends ComponentBaseProps {
  reportDateTime: string;
  title?: string;
  subTitle?: string;
  columns: [string, Record<keyof TableRowCommonKeys | keyof RowType, TableRowProperties>[string] | undefined][];
  rows: any[];
  pdfHeaderTextSmall?: boolean;
}

export interface TableAsyncPrintRowProps {
  row: any;
  index: number;
  columns: [string, TableRowProperties | undefined][];
  nonSpecifiedColumnWidth: number;
}

export type SimpleAccordionTableVariant = "primary" | "secondary";

export interface SimpleTableRow {
  title: string;
  value?: React.ReactNode;
  tooltip?: string;
  subContent?: SimpleTableRow[];
  emphasisValue?: boolean;
}
export interface SimpleAccordionTableProps {
  items: SimpleTableRow[];
  defaultExpanded?: boolean;
  variant?: SimpleAccordionTableVariant;
  emphasisValue?: boolean;
}

export interface SimpleAccordionTableRowProps extends SimpleTableRow {
  defaultExpanded?: boolean;
  variant?: SimpleAccordionTableVariant;
  emphasisValue?: boolean;
}

export interface SimpleAccordionTableRowContentProps {
  subContent?: SimpleTableRow[];
}

export interface TableActionButtonProps {
  icon: IconDefinition;
  onClickHandler?: () => Promise<any>;
  toolTipText?: string;
}

export interface TableActionsProps {
  actions: TableActionButtonProps[];
  size?: "small" | "large";
}

export interface AdaptiveDetailTableRowProperties<RowType> extends ComponentBaseProps {
  /** Column header text */
  header?: string | ReactElement;
  /** If column data needs to be formatted before displaying */
  formatter?: (values: RowType, rowIndex: number) => any;
  /** Alignment of the content */
  alignment?: ColumnAlignment;
  /** Can specify a static width for the column */
  width?: number | string;
  /** Can specify a max width for the column */
  maxWidth?: number | string;
  /** To avoid wrapping content when not enough space to display */
  noWrap?: boolean;
}

export interface AdaptiveDetailTableViewProps<RowType extends Record<string, any>> extends ComponentBaseProps {
  /** The data rows to display in the table */
  data: RowType[];
  /** Column definition */
  properties: Partial<Record<keyof RowType, AdaptiveDetailTableRowProperties<RowType>>>;
  /** Hide table header */
  hideHeader?: boolean;
  /** Columns to hide from display */
  hiddenColumns?: (keyof RowType)[];
  /** Add separators between rows */
  addRowSeparators?: boolean;
  /**
   * Returns a row's child rows. A row is expandable iff this returns a non-empty array (single level).
   * Expansion is driven by a whole-row click handler, so interactive content rendered by a column
   * `formatter` (links, buttons, tooltips) must call `stopPropagation` on its own events to avoid
   * also toggling row expansion.
   */
  getRowChildren?: (row: RowType) => RowType[] | undefined;
  /** Stable key for a row, used to track expansion state across re-renders/reloads. Defaults to row index. */
  getRowKey?: (row: RowType, index: number) => React.Key;
}
